import { getDB } from "./storageService";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { secureFetch } from "./batchSecurity";

/**
 * 🎯 DYNAMIC METADATA MAPPER
 */
const getAdditionalMetadata = (record) => {
  const systemKeys = [
    "batchId", "hash", "timestamp", "targetBucket", "targetRoot",
    "siteKey", "files", "status", "S3CorsValue", "bits",
    "type", "name", "size", "id"
  ];

  const metadata = {};

  if (!record) return metadata;

  Object.keys(record).forEach(key => {
    if (
      !systemKeys.includes(key) &&
      record[key] !== undefined &&
      record[key] !== null
    ) {
      const s3Key = key
        .replace(/([a-z])([A-Z])/g, "$1-$2")
        .toLowerCase();

      metadata[s3Key] = String(record[key]);
    }
  });

  return metadata;
};

/**
 * ACTION 2: Apply for the Signed URL.
 */
export const ApplyForSignedURL = async (batchId, fileNames, siteKey) => {
  const db = getDB(siteKey);
  const batch = await db.batches.where("batchId").equals(batchId).first();

  if (!batch?.S3CorsValue) {
    throw new Error(`FACILITY_REJECTED: Mandatory S3CorsValue ("esg") is missing.`);
  }

  const metadata = getAdditionalMetadata(batch);

  console.log("%c 🚀 Neil WOZ YERE | ACTION_2_PAYLOAD_GEN", "color: #ff00ff; font-weight: bold;");
  console.log("📦 Metadata being requested for signing:", metadata);

  const payload = {
    siteKey,
    batchId: parseInt(batchId),
    fileNames,
    providedHash: batch.hash,
    timestamp: String(batch.timestamp),
    bucketName: batch.targetBucket,
    rootFolder: batch.targetRoot,
    S3CorsValue: batch.S3CorsValue,
    origin: window.location.origin,
    metadata: metadata,
    AdditionalMetadata: metadata
  };

  console.log("📤 DISPATCHING_TO_BFF:", payload);

  const response = await secureFetch(
    `/api/v1/courier/get-signed-url`,
    "POST",
    siteKey,
    "Apply",
    payload
  );

  const result = GoldenSourceHelper.normalize(response);
  const uploadUrl = result.getProp("upload_Url");

  if (!uploadUrl) {
    throw new Error("GATEWAY_REJECTION: No signed URL returned.");
  }

  return uploadUrl;
};

/**
 * ACTION 3: Direct S3 Delivery.
 */
export const deliverFileToS3 = async (fileRecord, siteKey, onProgress) => {
  const fileName = fileRecord.name;
  const batchId = fileRecord.batchId;

  console.log(`%c 🛰️ STARTING_DELIVERY: ${fileName} (Batch: ${batchId})`, "color: #6610f2; font-weight: bold;");

  console.log(`%c 🔑 ACTION_2: Requesting JIT Authorization for ${fileName}...`, "color: #6c757d;");
  const uploadUrl = await ApplyForSignedURL(batchId, [fileName], siteKey);
  console.log(`%c ✅ AUTH_GRANTED: Permit received for ${fileName}`, "color: #28a745;");

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const data = fileRecord.bits || fileRecord;

    xhr.open("PUT", uploadUrl);

    /**
     * 🔐 SIGNATURE PARITY ENFORCEMENT
     *
     * We inspect X-Amz-SignedHeaders and ONLY send what was signed.
     * Anything extra = 403.
     */
    const urlObj = new URL(uploadUrl);
    const signedHeadersRaw = urlObj.searchParams.get("X-Amz-SignedHeaders") || "";
    const signedHeaders = signedHeadersRaw.split(";").map(h => h.trim().toLowerCase());

    console.groupCollapsed(`🔐 SIGNATURE_INSPECTION: ${fileName}`);
    console.log("SignedHeaders from URL:", signedHeadersRaw);
    console.log("Parsed SignedHeaders:", signedHeaders);

    const headersSent = [];

    // ✅ Content-Type parity
    if (signedHeaders.includes("content-type")) {
      const contentType = "application/octet-stream"; // MUST match Lambda presigner
      xhr.setRequestHeader("Content-Type", contentType);
      headersSent.push({ "Content-Type": contentType });
    }

    // ✅ Metadata parity (only if actually signed)
    const metadata = getAdditionalMetadata(fileRecord);

    const signedMetaHeaders = signedHeaders.filter(h => h.startsWith("x-amz-meta-"));

    if (signedMetaHeaders.length > 0) {
      Object.entries(metadata).forEach(([key, value]) => {
        const headerKey = `x-amz-meta-${key.toLowerCase()}`;

        if (signedMetaHeaders.includes(headerKey)) {
          xhr.setRequestHeader(headerKey, String(value));
          headersSent.push({ [headerKey]: String(value) });
        }
      });
    } else {
      console.log("⚠️ Metadata not signed. No x-amz-meta-* headers will be sent.");
    }

    console.log("Headers Actually Sent:", headersSent);
    console.groupEnd();

    // Progress
    // Inside deliverFileToS3 in batchDelivery.js
    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable && typeof onProgress === "function") {
        const percent = Math.round((e.loaded / e.total) * 100);
        // ⚡ PULSE: Sends bits-moved event up to the manager
        onProgress(percent, e.loaded);
      }
    });

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log(`%c 🏆 S3_SUCCESS: ${fileName} successfully persisted.`, "color: #28a745; font-weight: bold;");
          resolve();
        } else {
          console.error(`%c ❌ S3_FAILURE: ${fileName} | HTTP_${xhr.status}`, "background: #dc3545; color: #fff;");
          reject(new Error(`S3_HTTP_${xhr.status}`));
        }
      }
    };

    console.log(`%c 🚀 UPLOAD_DISPATCHED: Pushing bits for ${fileName}...`, "color: #fd7e14;");
    xhr.send(data);
  });
};